import React, { PureComponent } from 'react';
import { Formik, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import { Download } from '@hobs/utils/download';
import InputLabel from '@hobs/components/InputLabel';
import Input from '@hobs/components/Input';
import DateInput from '@hobs/components/DateInput';
import Icon from '@hobs/components/Icon';
import Dropdown from '@hobs/components/Dropdown';
import Textarea from '@hobs/components/Textarea';
import PageHeader from '@hobs/components/PageHeader';
import NumericInput from '@hobs/components/NumericInput';
import Toast from '@hobs/components/Toast';
import Confirm from '@hobs/components/Confirm';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import {
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  DATE_FORMAT,
  USER_ROLES
} from '@hobs/utils/constants';
import { format } from '@hobs/utils/formatWithTimeZone';
import { countDecimals } from '@hobs/utils/countDecimals';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import Panel from '@hobs/components/Panel';
import { H2 } from '@hobs/components/Headings';
import PageActions from '@hobs/components/PageActions';
import { PageLinks } from './PageLinks';
import './Transportation.scss';

class Transportation extends PureComponent {
  constructor(props) {
    super(props);

    const { t } = this.props;

    this.form = React.createRef();

    this.state = {
      detailedInformationFields: []
    };

    const getValue = (value, eq) => {
      return Boolean(value && value.toString() && value.toString() === eq);
    };

    this.validationSchema = Yup.object().shape({
      generalInformation: Yup.object().shape({
        serialNumber: Yup.string()
          .nullable()
          .max(15, t('Serial number cannot be longer than 15 symbols'))
      }),
      pickupDetails: Yup.object().shape({
        namePoc: Yup.string()
          .nullable()
          .max(256, t('Name POC cannot be longer than 256 symbols')),
        location: Yup.object().shape({
          name: Yup.string()
            .nullable()
            .max(256, t('Place of loading cannot be longer than 256 symbols'))
            .required(t('Place of loading is required')),
          zipCode: Yup.string()
            .nullable()
            .max(5, t('Zip code cannot be longer than 5 symbols'))
            .required(t('Zip code is required')),
          postAddress: Yup.string()
            .nullable()
            .max(50, t('Post. add. cannot be longer than 50 symbols'))
            .required(t('Postal address is required')),
          comment: Yup.string()
            .nullable()
            .max(256, t('Location comment cannot be longer than 256 symbols'))
        }),
        mobilePhone: Yup.string()
          .nullable()
          .required(t('Mobile phone is required')),
        email: Yup.string()
          .nullable()
          .email(t('Invalid email format'))
          .required(t('Email is required')),
        mission: Yup.string()
          .nullable()
          .max(256, t('Mission cannot be longer than 256 symbols'))
      }),
      deliveryDetails: Yup.object().shape({
        namePoc: Yup.string()
          .nullable()
          .max(256, t('Name POC cannot be longer than 256 symbols')),
        location: Yup.object().shape({
          name: Yup.string()
            .nullable()
            .max(256, t('Place of delivery cannot be longer than 256 symbols'))
            .required(t('Place of delivery is required')),
          zipCode: Yup.string()
            .nullable()
            .max(5, t('Zip code cannot be longer than 5 symbols'))
            .required(t('Zip code is required')),
          postAddress: Yup.string()
            .nullable()
            .max(50, t('Post. add. cannot be longer than 50 symbols'))
            .required(t('Postal address is required')),
          comment: Yup.string()
            .nullable()
            .max(256, t('Location comment cannot be longer than 256 symbols'))
        }),
        mobilePhone: Yup.string()
          .nullable()
          .required(t('Mobile phone is required')),
        email: Yup.string()
          .nullable()
          .email(t('Invalid email format')),
        remarks: Yup.string()
          .nullable()
          .max(256, t('Remarks cannot be longer than 256 symbols'))
      }),
      lines: Yup.array().of(
        Yup.object().shape({
          typeId: Yup.string()
            .nullable()
            .required(t('Item type is required'))
            .typeError(t('Item type is required')),
          description: Yup.string()
            .nullable()
            .max(30, t('Description cannot be longer than 30 symbols'))
            .required(t('Description is required')),
          quantity: Yup.number()
            .nullable()
            .min(1, t('Quantity must be greater than 0'))
            .required(t('Quantity is required'))
            .typeError(t('Quantity is required')),
          length: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid length format'))
                  .nullable()
                  .min(0.01, t('Length must be greater than 0'))
                  .test('format', t('Invalid length format'), val => {
                    return countDecimals(val) < 3;
                  })
              : Yup.number(t('Invalid length format'))
                  .test('format', t('Invalid length format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Length must be greater than 0'))
                  .required(t('Length is required'))
                  .typeError(t('Invalid length format'))
          ),
          width: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid width format'))
                  .test('format', t('Invalid width format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Width must be greater than 0'))
              : Yup.number(t('Invalid width format'))
                  .test('format', t('Invalid width format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Width must be greater than 0'))
                  .required(t('Width is required'))
                  .typeError(t('Invalid width format'))
          ),
          height: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid height format'))
                  .test('format', t('Invalid height format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Height must be greater than 0'))
              : Yup.number(t('Invalid height format'))
                  .nullable()
                  .test('format', t('Invalid height format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .min(0.01, t('Height must be greater than 0'))
                  .required(t('Height is required'))
                  .typeError(t('Invalid height format'))
          ),
          weight: Yup.string().when('typeId', value =>
            getValue(value, '2')
              ? Yup.number(t('Invalid weight format'))
                  .test('format', t('Invalid weight format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Weight must be greater than 0'))
              : Yup.number(t('Invalid weight format'))
                  .test('format', t('Invalid weight format'), val => {
                    return countDecimals(val) < 3;
                  })
                  .nullable()
                  .min(0.01, t('Weight must be greater than 0'))
                  .required(t('Weight is required'))
                  .typeError(t('Invalid weight format'))
          ),
          dgrName: Yup.string().when('typeId', value =>
            getValue(value, '2') || getValue(value, '3')
              ? Yup.string()
                  .nullable()
                  .max(30, t('DGR name cannot be longer than 30 symbols'))
              : Yup.string()
                  .nullable()
                  .max(30, t('DGR name cannot be longer than 30 symbols'))
                  .required(t('DGR name is required'))
          ),
          unNr: Yup.string().when('typeId', value =>
            getValue(value, '2') || getValue(value, '3')
              ? Yup.string().nullable()
              : Yup.string()
                  .nullable()
                  .test(
                    'format',
                    t('Invalid UN-NR format'),
                    val => val && val.toString().length === 4
                  )
                  .required(t('UN-NR is required'))
          ),
          dgrClass: Yup.number().when('typeId', value =>
            getValue(value, '2') || getValue(value, '3')
              ? Yup.number().nullable()
              : Yup.number()
                  .typeError(t('DGR class is required'))
                  .nullable()
                  .test('format', t('Invalid DGR class format'), val => {
                    return val > 1 && val < 10 && countDecimals(val) < 3;
                  })
                  .required(t('DGR class is required'))
          ),
          neq: Yup.string().when('typeId', value =>
            !getValue(value, '2') && !getValue(value, '3')
              ? Yup.number(t('Invalid NEQ format'))
                  .nullable()
                  .test('format', t('Invalid NEQ format'), val => {
                    return val >= 0 && countDecimals(val) < 3;
                  })
                  .min(0, t('NEQ cannot be less than 0'))
                  .required(t('NEQ is required'))
                  .typeError(t('Invalid NEQ format'))
              : Yup.number().nullable()
          ),
          remarks: Yup.string()
            .nullable()
            .max(50, t('Remarks cannot be longer than 50 symbols'))
        })
      )
    });
  }

  async componentDidMount() {
    const {
      match: {
        params: { serviceRequestId }
      },
      get,
      forSubmit
    } = this.props;

    await get(serviceRequestId);
    await forSubmit();
  }

  componentWillUnmount() {
    this.props.reset();
  }

  // packages/@hobs/utils/constants/constants.js STATUSES[2] - removed
  isDisabled() {
    return this.props.transportation.isDisabled;
  }

  getDiscussionPath() {
    const { match } = this.props;
    return `${match.url}/discussion`;
  }

  getBackNavigationUrl = (navContext, history) => {
    const { match } = this.props;
    if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview) {
      const withSearch =
        history && history.location && history.location.search
          ? history.location.search
          : '';
      return `/annex-overview${withSearch}`;
    } else if (
      navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices
    ) {
      return `/service-requests/${match.params.partyId}`;
    } else if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets) {
      return '/tickets';
    }

    return `/service-requests/${match.params.partyId}`;
  };

  navigateBack = (navContext, previousPageHistory) => () => {
    const { history } = this.props;
    history.push(this.getBackNavigationUrl(navContext, previousPageHistory));
  };

  isOnlyPreviewMode() {
    const { hasUserPermission, transportation } = this.props;
    return (
      hasUserPermission(USER_ROLES.READONLY) ||
      (hasUserPermission(USER_ROLES.HOST_UNIT) &&
        !transportation.canHostUnitEdit)
    );
  }

  renderTransportationInfo(values) {
    const { t, forSubmitData } = this.props;

    return (
      <div className="transportation--fields-row">
        <div className="transportation--field">
          <Field
            component={Dropdown}
            name="transportInformation.vehicleTypeId"
            label={t('Vehicle type')}
            disabled={
              forSubmitData.vehicleTypes.length === 0 ||
              this.isOnlyPreviewMode()
            }
          >
            <option value="">{t('Please select')}</option>
            {forSubmitData.vehicleTypes.map(item => {
              return (
                <option key={item.vehicleTypeId} value={item.vehicleTypeId}>
                  {item.vehicleType}
                </option>
              );
            })}
          </Field>
        </div>
        <div className="transportation--field">
          <Field
            component={Dropdown}
            name="transportInformation.transportTypeId"
            label={t('Transportation type')}
            disabled={
              forSubmitData.transportTypes.length === 0 ||
              this.isOnlyPreviewMode()
            }
          >
            <option value="">{t('Please select')}</option>
            {forSubmitData.transportTypes.map(item => {
              return (
                <option
                  key={item.transportationTypeId}
                  value={item.transportationTypeId}
                >
                  {item.transportationType}
                </option>
              );
            })}
          </Field>
        </div>
        <div className="transportation--field">
          <Field
            component={Dropdown}
            name="transportInformation.supplierName"
            label={t('Supplier')}
            disabled={
              forSubmitData.suppliers.length === 0 || this.isOnlyPreviewMode()
            }
          >
            {forSubmitData.suppliers.length === 0 && (
              <option value="">{t('There are no subcontractors')}</option>
            )}

            {values.transportInformation.supplierName !== '' &&
              !forSubmitData.suppliers.find(
                item =>
                  item.supplierName === values.transportInformation.supplierName
              ) && (
                <option value={values.transportInformation.supplierName}>
                  {values.transportInformation.supplierName}
                </option>
              )}
            <option value="">{t('Please select')}</option>
            {forSubmitData.suppliers.map(item => {
              return (
                <option key={item.supplierId} value={item.supplierName}>
                  {item.supplierName}
                </option>
              );
            })}
          </Field>
        </div>
      </div>
    );
  }

  onSubmit = (values, { setSubmitting }) => {
    const {
      match: {
        params: { serviceRequestId }
      },
      t,
      get
    } = this.props;

    const { pickupDetails, deliveryDetails } = values;

    const data = {
      ...values,
      pickupDetails: {
        ...pickupDetails,
        transportationTime: pickupDetails.transportationTime
          ? {
              hour: pickupDetails.transportationTime.hour,
              minute: pickupDetails.transportationTime.minute,
              date:
                pickupDetails.transportationTime.date !== '-'
                  ? pickupDetails.transportationTime.date
                  : null
            }
          : null
      },
      deliveryDetails: {
        ...deliveryDetails,
        transportationTime: deliveryDetails.transportationTime
          ? {
              hour: deliveryDetails.transportationTime.hour,
              minute: deliveryDetails.transportationTime.minute,
              date:
                deliveryDetails.transportationTime.date !== '-'
                  ? deliveryDetails.transportationTime.date
                  : null
            }
          : null
      }
    };

    this.props
      .update(this.props.match.params.serviceRequestId, data)
      .then(response => {
        if (response.success) {
          Toast.success(t('Transportation details updated successfully'));

          get(serviceRequestId);
          setSubmitting(false);
        } else {
          Toast.warning(response.payload.message);
          setSubmitting(false);
        }
      });
  };

  convertObjectNullsToString(object) {
    return Object.keys(object).reduce((result, item) => {
      if (result[item] === null) {
        result[item] = '';
      }

      return result;
    }, object);
  }

  getPaddedTime(time) {
    if (time != null && time < 10 && (!time.length || time.length <= 1)) {
      return `0${time}`;
    }
    return time;
  }

  setTransportationTime(transportationTime) {
    if (transportationTime) {
      transportationTime.hour = this.getPaddedTime(transportationTime.hour);
      transportationTime.minute = this.getPaddedTime(transportationTime.minute);
    }
    return transportationTime;
  }

  getInitialValues() {
    const {
      transportation,
      transportation: { deliveryDetails, pickupDetails, transportInformation }
    } = this.props;

    pickupDetails.transportationTime = this.setTransportationTime(
      pickupDetails.transportationTime
    );
    deliveryDetails.transportationTime = this.setTransportationTime(
      deliveryDetails.transportationTime
    );

    return {
      ...this.convertObjectNullsToString(transportation),
      generalInformation: this.convertObjectNullsToString(
        transportation.generalInformation
      ),
      pickupDetails: {
        ...this.convertObjectNullsToString(pickupDetails),
        transportationTime: pickupDetails.transportationTime
          ? {
              ...pickupDetails.transportationTime
            }
          : {
              date: '',
              hour: '',
              minute: ''
            }
      },
      deliveryDetails: {
        ...this.convertObjectNullsToString(deliveryDetails),
        transportationTime: deliveryDetails.transportationTime
          ? {
              ...this.convertObjectNullsToString(
                deliveryDetails.transportationTime
              )
            }
          : {
              date: '',
              hour: '',
              minute: ''
            },
        remarks: deliveryDetails.remarks ? deliveryDetails.remarks : ''
      },
      transportInformation: transportInformation
        ? this.convertObjectNullsToString(transportInformation)
        : {
            vehicleType: '',
            vehicleTypeId: '',
            transportType: '',
            transportTypeId: '',
            supplierName: '',
            supplierId: ''
          }
    };
  }

  resetForm = resetForm => {
    const { t } = this.props;

    Confirm(
      t('Cancel'),
      t('Are you sure you want to cancel? All entered data will be lost'),
      () => {
        // WARNING: HACK DETECTED ð¿ ð
        // Strange that formik is not resseting field
        // displayed values to initialValues
        // So we have to do this strange hack (native js reset using react refs)
        if (this.form.current) {
          this.form.current.reset();
          resetForm(this.getInitialValues());
        }
      }
    );
  };

  onBackClick = (event, dirty, nav) => {
    event.preventDefault();
    const { history } = this.props;

    if (!dirty) {
      history.push(
        this.getBackNavigationUrl(
          nav.serviceRequestNavigation,
          nav.previousPageHistory
        )
      );
    } else {
      Confirm(
        this.props.t('Cancel service request'),
        this.props.t(
          'Are you sure you want to cancel? All entered data will be lost.'
        ),
        () => {
          history.push(
            this.getBackNavigationUrl(
              nav.serviceRequestNavigation,
              nav.previousPageHistory
            )
          );
        }
      );
    }
  };

  renderTransportationHeading(dirty) {
    const { t, transportation } = this.props;

    return (
      <ServiceRequestNavContext.Consumer>
        {nav => (
          <>
            {this.props.withEscNavigationCallback(
              this.navigateBack(
                nav.serviceRequestNavigation,
                nav.previousPageHistory
              )
            )}
            <PageHeader
              onClick={e => this.onBackClick(e, dirty, nav)}
              title={t('Transportation details')}
              unit={{
                code: transportation.partyCode,
                name: transportation.unitName,
                number: transportation.requestNumber
              }}
              status={{
                id: transportation.statusId,
                name: transportation.statusName
              }}
            />
          </>
        )}
      </ServiceRequestNavContext.Consumer>
    );
  }
  renderPageLinks(dirty) {
    const { t, match, get } = this.props;

    return (
      <PageLinks
        isDirty={dirty}
        isTransportation={true}
        partyId={match.params.partyId}
        isCustom={false}
        serviceRequestId={match.params.serviceRequestId}
        discussion={{
          status: this.props.transportation.discussionStatusId,
          initials: t('SN'),
          discussionPath: this.getDiscussionPath(),
          onDiscussionClose: () => get(match.params.serviceRequestId),
          isSeen: this.props.transportation.isLastDiscussionSeen
        }}
      />
    );
  }

  exportTransporation = () => {
    const {
      exportExcel,
      match: {
        params: { serviceRequestId }
      }
    } = this.props;

    exportExcel(serviceRequestId).then(response => {
      if (!response.status) {
        Download(response, 'Transportation.xlsx');
      } else {
        Toast.warning(response.payload.message);
      }
    });
  };

  renderExportButton() {
    const { t, transportation } = this.props;

    return (
      <GhostButton
        disabled={
          transportation &&
          transportation.pickupDetails &&
          transportation.pickupDetails.location &&
          !transportation.pickupDetails.location.zipCode
        }
        iconLeft={<Icon icon="download" />}
        onClick={this.exportTransporation}
      >
        {t('XLSX')}
      </GhostButton>
    );
  }

  timeCheck = (minutes = false) => event => {
    const {
      target: { value, selectionStart },
      charCode
    } = event;

    const allowedFirstNumber = minutes
      ? selectionStart === 0 &&
        [48, 49, 50, 51, 52, 53].indexOf(charCode) === -1
      : [48, 49, 50].indexOf(charCode) === -1 && selectionStart === 0;

    const allowedSecondNumber = minutes
      ? false
      : selectionStart === 1 &&
        (parseInt(value, 10) === 2 &&
          [48, 49, 50, 51].indexOf(charCode) === -1);

    if (
      [48, 49, 50, 51, 52, 53, 54, 55, 56, 57].indexOf(charCode) === -1 ||
      value > 59 ||
      allowedFirstNumber ||
      allowedSecondNumber ||
      value.length === 2
    ) {
      event.preventDefault();
    }
  };

  renderGeneralInformation() {
    const { t } = this.props;

    return (
      <Panel>
        <div className="transportation--panel">
          <H2>{t('General information')}</H2>

          <div className="transportation--fields-row">
            <div className="transportation--field">
              <Field
                component={Input}
                placeholder={t('Serial number')}
                name="generalInformation.serialNumber"
                disabled={this.isOnlyPreviewMode()}
              >
                {t('Serial number')}
              </Field>
            </div>
            <div className="transportation--field">
              <Field
                component={Input}
                placeholder={t('PO number')}
                name="generalInformation.poNumber"
                disabled={this.isOnlyPreviewMode()}
              >
                {t('PO number')}
              </Field>
            </div>
            <div className="transportation--field">
              <InputLabel>{t('Last update')}</InputLabel>
              <div>
                {format(
                  this.props.transportation.generalInformation.modifiedDate,
                  DATE_FORMAT
                )}
              </div>
            </div>
          </div>
        </div>
      </Panel>
    );
  }

  renderHeaderCenterActions = () => {
    return null;
  };

  render() {
    const { t, forSubmitData } = this.props;

    return (
      <Formik
        initialValues={this.getInitialValues()}
        enableReinitialize={true}
        validationSchema={this.validationSchema}
        onSubmit={this.onSubmit}
      >
        {({
          handleSubmit,
          resetForm,
          isSubmitting,
          dirty,
          values,
          setFieldValue
        }) => {
          return (
            <>
              <div className="transportation--heading">
                {this.renderTransportationHeading(dirty)}
                {this.renderHeaderCenterActions()}
                <div className="transportation--heading-right">
                  {this.renderExportButton()}
                  {this.renderPageLinks(dirty)}
                </div>
              </div>
              <form
                className="transportation"
                onSubmit={handleSubmit}
                ref={this.form}
              >
                <fieldset disabled={this.isDisabled()}>
                  {!this.isDisabled() && (
                    <PageActions container={this.form.current}>
                      <div />
                      <div>
                        <GhostButton
                          large
                          onClick={() => {
                            this.resetForm(resetForm);
                          }}
                          disabled={
                            isSubmitting || !dirty || this.isOnlyPreviewMode()
                          }
                        >
                          {t('Cancel')}
                        </GhostButton>
                        <RaisedButton
                          large
                          type="submit"
                          disabled={
                            isSubmitting || !dirty || this.isOnlyPreviewMode()
                          }
                        >
                          {t('Save')}
                        </RaisedButton>
                      </div>
                    </PageActions>
                  )}
                  <div className="transportation--columns">
                    <div>
                      {this.renderGeneralInformation()}
                      <Panel>
                        <div className="transportation--panel">
                          <H2>{t('Pickup details')}</H2>
                          <div className="transportation--panel-columns">
                            <div className="transportation--panel-columns-left">
                              <Field
                                component={Input}
                                placeholder={t('Place of loading')}
                                name="pickupDetails.location.name"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Place of loading')}
                              </Field>

                              <div className="transportation--fields-row">
                                <Field
                                  component={Input}
                                  placeholder={t('Latitude')}
                                  name="pickupDetails.location.latitude"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Latitude')}
                                </Field>
                                <Field
                                  component={Input}
                                  placeholder={t('Longitude')}
                                  name="pickupDetails.location.longitude"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Longitude')}
                                </Field>
                              </div>
                              <div className="transportation--fields-row">
                                <Field
                                  component={Input}
                                  placeholder={t('Zip code')}
                                  name="pickupDetails.location.zipCode"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Zip code')}
                                </Field>
                                <Field
                                  component={Input}
                                  placeholder={t('Post. add.')}
                                  name="pickupDetails.location.postAddress"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Post. add.')}
                                </Field>
                              </div>
                              <Field
                                component={Input}
                                placeholder={t('Location comment')}
                                name="pickupDetails.location.comment"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Location comment')}
                              </Field>
                            </div>

                            <div className="transportation--panel-columns-right">
                              <Field
                                component={Input}
                                placeholder={t('Name POC')}
                                name="pickupDetails.namePoc"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Name POC')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Mobile phone')}
                                name="pickupDetails.mobilePhone"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Mobile phone')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Phone')}
                                name="pickupDetails.phone"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Phone')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('E-mail')}
                                name="pickupDetails.email"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('E-mail')}
                              </Field>
                            </div>
                          </div>

                          <div className="transportation--fields-row">
                            <Field
                              component={DateInput}
                              setFieldValue={setFieldValue}
                              name="pickupDetails.transportationTime.date"
                              disabled={this.isOnlyPreviewMode()}
                            >
                              {t('Earliest date')}
                            </Field>
                            <div className="transportation--fields-time">
                              <Field
                                className="transportation--field-time"
                                component={Input}
                                placeholder={t('HH')}
                                onKeyPress={this.timeCheck()}
                                onPaste={event => {
                                  event.preventDefault();
                                }}
                                disabled={this.isOnlyPreviewMode()}
                                name="pickupDetails.transportationTime.hour"
                              >
                                {t('Time')}
                              </Field>
                              <Field
                                className="transportation--field-time"
                                component={Input}
                                placeholder={t('mm')}
                                onKeyPress={this.timeCheck(true)}
                                onPaste={event => {
                                  event.preventDefault();
                                }}
                                disabled={this.isOnlyPreviewMode()}
                                name="pickupDetails.transportationTime.minute"
                              >
                                &nbsp;
                              </Field>
                            </div>
                          </div>

                          <div>
                            <Field
                              component={Input}
                              placeholder={t('Mission')}
                              name="pickupDetails.mission"
                              disabled={this.isOnlyPreviewMode()}
                            >
                              {t('Mission')}
                            </Field>
                          </div>
                        </div>
                      </Panel>
                    </div>

                    <div>
                      <Panel>
                        <div className="transportation--panel">
                          <H2>{t('Delivery details')}</H2>

                          <div className="transportation--panel-columns">
                            <div className="transportation--panel-columns-left">
                              <Field
                                component={Input}
                                placeholder={t('Place of delivery')}
                                name="deliveryDetails.location.name"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Place of delivery')}
                              </Field>

                              <div className="transportation--fields-row">
                                <Field
                                  component={Input}
                                  placeholder={t('Latitude')}
                                  name="deliveryDetails.location.latitude"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Latitude')}
                                </Field>
                                <Field
                                  component={Input}
                                  placeholder={t('Longitude')}
                                  name="deliveryDetails.location.longitude"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Longitude')}
                                </Field>
                              </div>

                              <div className="transportation--fields-row">
                                <Field
                                  component={Input}
                                  placeholder={t('Zip code')}
                                  name="deliveryDetails.location.zipCode"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Zip code')}
                                </Field>
                                <Field
                                  component={Input}
                                  placeholder={t('Post. add.')}
                                  name="deliveryDetails.location.postAddress"
                                  disabled={this.isOnlyPreviewMode()}
                                >
                                  {t('Post. add.')}
                                </Field>
                              </div>
                              <Field
                                component={Input}
                                placeholder={t('Location comment')}
                                name="deliveryDetails.location.comment"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Location comment')}
                              </Field>
                            </div>

                            <div className="transportation--panel-columns-right">
                              <Field
                                component={Input}
                                placeholder={t('Name POC')}
                                name="deliveryDetails.namePoc"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Name POC')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Mobile phone')}
                                name="deliveryDetails.mobilePhone"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Mobile phone')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('Phone')}
                                name="deliveryDetails.phone"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Phone')}
                              </Field>
                              <Field
                                component={Input}
                                placeholder={t('E-mail')}
                                name="deliveryDetails.email"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('E-mail')}
                              </Field>
                            </div>
                          </div>

                          <div className="transportation--fields-row">
                            <Field
                              component={DateInput}
                              setFieldValue={setFieldValue}
                              name="deliveryDetails.transportationTime.date"
                              disabled={this.isOnlyPreviewMode()}
                            >
                              {t('Latest date')}
                            </Field>
                            <div className="transportation--fields-time">
                              <Field
                                className="transportation--field-time"
                                component={Input}
                                onKeyPress={this.timeCheck()}
                                onPaste={event => {
                                  event.preventDefault();
                                }}
                                placeholder={t('HH')}
                                name="deliveryDetails.transportationTime.hour"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                {t('Time')}
                              </Field>
                              <Field
                                className="transportation--field-time"
                                component={Input}
                                onKeyPress={this.timeCheck(true)}
                                onPaste={event => {
                                  event.preventDefault();
                                }}
                                placeholder={t('mm')}
                                name="deliveryDetails.transportationTime.minute"
                                disabled={this.isOnlyPreviewMode()}
                              >
                                &nbsp;
                              </Field>
                            </div>
                          </div>
                          <div>
                            <Field
                              component={Textarea}
                              rows={6}
                              name="deliveryDetails.remarks"
                              placeholder={t('Remarks')}
                              label={t('Remarks')}
                              disabled={this.isOnlyPreviewMode()}
                            />
                          </div>

                          {this.renderTransportationInfo(values)}
                        </div>
                      </Panel>
                    </div>
                  </div>

                  <div>
                    <H2>{t('Detailed information')}</H2>
                    <div>
                      <FieldArray
                        name="lines"
                        render={arrayHelpers => {
                          return (
                            <>
                              <table className="table">
                                <thead>
                                  {values.lines.length > 0 && (
                                    <tr>
                                      <th width={53}>{t('No.')}</th>
                                      <th width={76}>{t('Type')}</th>
                                      <th width={260}>
                                        {t('Description of goods/passenger')}
                                      </th>
                                      <th width={66}>{t('Q-ty')}</th>
                                      <th width={66}>{t('Length cm')}</th>
                                      <th width={66}>{t('Width cm')}</th>
                                      <th width={66}>{t('Height cm')}</th>
                                      <th width={66}>{t('Weight kg')}</th>
                                      <th width={127}>{t('DGR name')}</th>
                                      <th width={66}>{t('UN-NR')}</th>
                                      <th width={66}>{t('DGR class')}</th>
                                      <th width={66}>{t('NEQ kg')}</th>
                                      <th width={66}>{t('Remarks')}</th>
                                      {!this.isDisabled() && <th></th>}
                                    </tr>
                                  )}
                                </thead>
                                <tbody>
                                  {values.lines.map((item, index) => {
                                    const typeId = item.typeId
                                      ? item.typeId.toString()
                                      : '';

                                    return (
                                      <tr key={`key_${index}_tr`}>
                                        <td>{index + 1}</td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 76
                                            }}
                                            setFieldValue={setFieldValue}
                                            component={Dropdown}
                                            disabled={this.isOnlyPreviewMode()}
                                            name={`lines[${index}].typeId`}
                                          >
                                            <option value="">
                                              {t('Not set')}
                                            </option>
                                            {forSubmitData.transportationDetailsTypes.map(
                                              item => {
                                                return (
                                                  <option
                                                    key={ShortId.generate()}
                                                    value={item.id}
                                                  >
                                                    {item.longName}
                                                  </option>
                                                );
                                              }
                                            )}
                                          </Field>
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 260
                                            }}
                                            disabled={this.isOnlyPreviewMode()}
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].description`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={this.isOnlyPreviewMode()}
                                            setFieldValue={setFieldValue}
                                            component={
                                              this.isOnlyPreviewMode()
                                                ? Input
                                                : NumericInput
                                            }
                                            name={`lines[${index}].quantity`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={typeId === '2'}
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].length`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={typeId === '2'}
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].width`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={typeId === '2'}
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].height`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={typeId === '2'}
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].weight`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={
                                              typeId === '2' || typeId === '3'
                                            }
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].dgrName`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={
                                              typeId === '2' || typeId === '3'
                                            }
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].unNr`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={
                                              typeId === '2' || typeId === '3'
                                            }
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].dgrClass`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={
                                              typeId === '2' || typeId === '3'
                                            }
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].neq`}
                                          />
                                        </td>
                                        <td>
                                          <Field
                                            noMargin
                                            style={{
                                              width: 66
                                            }}
                                            disabled={this.isOnlyPreviewMode()}
                                            setFieldValue={setFieldValue}
                                            component={Input}
                                            name={`lines[${index}].remarks`}
                                          />
                                        </td>
                                        {!this.isDisabled() &&
                                          !this.isOnlyPreviewMode() && (
                                            <td className="empty">
                                              <GhostButton
                                                className="transportation--remove-button"
                                                onClick={() => {
                                                  Confirm(
                                                    t('Delete'),
                                                    t(
                                                      '	Are you sure you want to delete this item?'
                                                    ),
                                                    () => {
                                                      arrayHelpers.remove(
                                                        index
                                                      );
                                                    }
                                                  );
                                                }}
                                              >
                                                <Icon icon="delete" />
                                              </GhostButton>
                                            </td>
                                          )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              <GhostButton
                                iconLeft={<Icon icon="Plus" />}
                                type="button"
                                disabled={
                                  this.isDisabled() || this.isOnlyPreviewMode()
                                }
                                onClick={() =>
                                  arrayHelpers.push({
                                    typeId: undefined,
                                    description: undefined,
                                    quantity: undefined,
                                    length: undefined,
                                    width: undefined,
                                    height: undefined,
                                    weight: undefined,
                                    unNr: undefined,
                                    dgrName: undefined,
                                    dgrClass: undefined,
                                    neq: undefined,
                                    remarks: undefined
                                  })
                                }
                              >
                                {t('Add new item')}
                              </GhostButton>
                            </>
                          );
                        }}
                      />
                    </div>
                  </div>
                </fieldset>
              </form>
            </>
          );
        }}
      </Formik>
    );
  }
}

Transportation.propTypes = {
  match: PropTypes.object,
  transportation: PropTypes.object,
  t: PropTypes.func,
  forSubmit: PropTypes.func,
  forSubmitData: PropTypes.object,
  history: PropTypes.object,
  get: PropTypes.func,
  update: PropTypes.func,
  exportExcel: PropTypes.func,
  hasUserPermission: PropTypes.func,
  reset: PropTypes.func,
  withEscNavigationCallback: PropTypes.func
};

export default Transportation;
